import React from "react";
import PropTypes from "prop-types";
import "../../assets/scss/custom/components/CustomButton.scss";

/**
 * CustomButton
 * Reusable button for auth screens.
 *
 * variant="primary"   -> solid orange gradient CTA, optional trailing arrow icon
 * variant="secondary" -> light gray outline button (e.g. "Continue with Google")
 * variant="blue"      -> solid blue button
 *
 * Usage:
 * <CustomButton type="submit" variant="primary" icon="arrow">Create Account</CustomButton>
 * <CustomButton variant="secondary" icon={<GoogleIcon />} onClick={...}>Continue with Google</CustomButton>
 */
const CustomButton = ({
  children,
  variant = "primary",
  type = "button",
  icon,
  iconPosition = "right",
  onClick,
  disabled,
  loading,
  className,
  ...rest
}) => {
  const renderIcon = () => {
    if (!icon) return null;
    return <img src={icon} alt="" className="custom-btn-icon-circle" />;
  };

  const noIconClass = !icon || loading ? " custom-btn-no-icon" : "";

  return (
    <button
      type={type}
      className={`custom-btn custom-btn-${variant}${noIconClass} ${className || ""}`}
      onClick={onClick}
      disabled={disabled || loading}
      {...rest}
    >
      {loading ? (
        <span className="custom-btn-spinner" aria-hidden="true" />
      ) : (
        <>
          {iconPosition === "left" && renderIcon()}
          <span className="custom-btn-label">{children}</span>
          {iconPosition === "right" && renderIcon()}
        </>
      )}
    </button>
  );
};

CustomButton.propTypes = {
  children: PropTypes.node,
  variant: PropTypes.oneOf(["primary", "secondary", "blue"]),
  type: PropTypes.string,
  icon: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
  iconPosition: PropTypes.oneOf(["left", "right"]),
  onClick: PropTypes.func,
  disabled: PropTypes.bool,
  loading: PropTypes.bool,
  className: PropTypes.string,
};

export default CustomButton;
